import { useState } from 'react'
import type { Room, Member } from '../../types/game'
import { startGame, transferMember } from '../../api/rooms'
import { useGameStore } from '../../store/gameStore'
import { ApiError } from '../../api/client'
import { assetUrl } from '../../utils/assets'

interface WaitingRoomProps {
  room: Room
  member: Member | null
  shellMode: 'desktop' | 'mobile'
}

const COLORS = ['orange', 'red', 'yellow', 'white'] as const

export function WaitingRoom({ room, member }: WaitingRoomProps) {
  const { memberId, setRoomId, setRoom, setError } = useGameStore()
  const [isStarting, setIsStarting] = useState(false)

  const isHost = member?.id === room.host_id

  const canStart = () => {
    if (!isHost) return false
    const humanCount = room.members.filter((knownMember) => !knownMember.is_bot).length
    const botCount = room.members.filter((knownMember) => knownMember.is_bot).length
    return humanCount > 1 || botCount > 0
  }

  const handleStart = async () => {
    if (!isHost) return
    setIsStarting(true)
    try {
      await startGame(room.id, memberId)
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
      else setError('Failed to start game')
    } finally {
      setIsStarting(false)
    }
  }

  const handleLeave = async () => {
    try {
      await transferMember({
        member_id: memberId,
        source_room_id: room.id,
        target_room_id: '000-000-000',
        delete_empty_source: true,
      })
      setRoomId('000-000-000')
    } catch {
      setRoomId(null)
      setRoom(null)
    }
  }

  return (
    <>
      <div className="lobby-overlay" />
      <div className="lobby tableau">
        <div className="lobby-header">
          <div className="lobby-title">{room.name}</div>
        </div>

        <div className="lobby-slot-list">
          {COLORS.map((color) => {
            const connectedMember = room.members.find((knownMember) => knownMember.color === color)
            const slotState = room.slot_states[color] ?? 'open'
            const label = connectedMember?.alias ?? `${slotState[0].toUpperCase()}${slotState.slice(1)}`

            return (
              <div key={color} className="lobby-slot">
                <div className="lobby-member-header">
                  <div className="opponent-identity">
                    <div
                      className="opponent-marker"
                      style={{ background: color === 'white' ? 'var(--white)' : `var(--${color})` }}
                    />
                    <div className="opponent-id">{color[0].toUpperCase() + color.slice(1)}</div>
                  </div>
                  {connectedMember?.is_bot ? (
                    <div className="lobby-slot-badge bot">
                      <div className="lobby-slot-badge-knob" />
                      <div>Bot</div>
                    </div>
                  ) : connectedMember?.is_host ? (
                    <div className="lobby-slot-badge host">
                      <img src={assetUrl('images', 'host.svg')} alt="Host" className="host-icon" />
                      <div>Host</div>
                    </div>
                  ) : !connectedMember && slotState === 'closed' ? (
                    <div className="lobby-robot-checkbox">Closed</div>
                  ) : null}
                </div>
                <input type="text" className="input" value={label} disabled readOnly />
              </div>
            )
          })}
        </div>

        <div className="lobby-header">
          <div>Resource offers are {room.are_offers_public ? 'public' : 'private'}</div>
          <div>Unlimited Queen Trade is {room.unlimited_queen_trade ? 'enabled' : 'disabled'}</div>
          <div>
            {room.donation_mode === 'disabled'
              ? 'Donations are disabled'
              : room.donation_mode === 'refusable'
                ? 'Donations are refusable'
                : 'Donations are non refusable'}
          </div>
          <div>Eggs hatch {room.egg_incubation === 'year' ? 'after winter' : 'every season'}</div>
          <div>Brood Chamber cost: {room.incremental_brood_chamber_cost ? 'incremental (1/2/3 wax by ring)' : 'flat (1 wax)'}</div>
          <div>Infections {room.infection_spread !== false ? 'spread toward the Queen' : 'do not spread'}</div>
          <div>
            Win: {room.win_condition !== 'full_honey'
              ? "hive fully built and infection-free at Winter's end"
              : 'every cell filled with honey'}
          </div>
          <div>
            Survivor favor: {room.survivor_favor_mode === 'per_resource'
              ? '1 per resource that counted'
              : 'largest offering 5, second 3'}
          </div>
        </div>

        <div className="lobby-start-container">
          <div id="waiting-room-leave-button" className="lobby-start" onClick={handleLeave}>
            Leave
          </div>
          <div
            id="waiting-room-start-button"
            className={canStart() && !isStarting ? 'lobby-start' : 'lobby-start disabled'}
            onClick={canStart() && !isStarting ? handleStart : undefined}
          >
            {isStarting ? 'Starting...' : 'Start'}
          </div>
        </div>
      </div>
    </>
  )
}
